import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Capture UTM params from URL on app load (forwarded from marketing site)
(function captureUTM() {
  try {
    var params = new URLSearchParams(window.location.search);
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'landing_page'].forEach(function(key) {
      var val = params.get(key);
      if (val) sessionStorage.setItem(key, val);
    });
  } catch (e) {
    // sessionStorage not available
  }
})();

// Auth Components
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import DriverSignup from './components/Auth/DriverSignup';
import RiderSignup from './components/Auth/RiderSignup';
import VerifyEmail from './components/Auth/VerifyEmail';
import RequestPasswordReset from './components/Auth/RequestPasswordReset';
import ResetPassword from './components/Auth/ResetPassword';

// Driver Components
import DriverDashboard from './components/Driver/Dashboard';
import DriverProfile from './components/Driver/Profile';
import DriverAvailability from './components/Driver/Availability';
import DriverPricing from './components/Driver/Pricing';
import DayRatePricing from './components/Driver/DayRatePricing';
import DriverRideRequests from './components/Driver/RideRequests';
import DriverCalendar from './components/Driver/Calendar';
import SubscriptionManagement from './components/Driver/SubscriptionManagement';
import DriverRideHistory from './components/Driver/RideHistory';
import StripeOnboarding from './components/Driver/StripeOnboarding';
import StripeOnboardingComplete from './components/Driver/StripeOnboardingComplete';
import PayoutHistory from './components/Driver/PayoutHistory';
import DriverOnboarding from './components/Driver/Onboarding/DriverOnboarding';
import ProfileSetup from './components/Driver/Onboarding/ProfileSetup';
import DriverHelpSupport from './components/Driver/HelpSupport';

// Rider Components
import RiderDashboard from './components/Rider/Dashboard';
import RiderProfile from './components/Rider/Profile';
import BrowseDrivers from './components/Rider/BrowseDrivers';
import RequestRide from './components/Rider/RequestRide';
import RequestDayBooking from './components/Rider/RequestDayBooking';
import RiderRideHistory from './components/Rider/RideHistory';
import RiderHelpSupport from './components/Rider/HelpSupport';

// Shared Components
import ProfileSettings from './pages/ProfileSettings';
import RideCompletionPage from './components/RideCompletionPage';
import RideTipPage from './components/RideTipPage';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DriverTermsOfUse from './pages/DriverTermsOfUse';
import RiderTermsOfUse from './pages/RiderTermsOfUse';
import EmailChangePending from './pages/EmailChangePending';
import VerifyEmailChange from './pages/VerifyEmailChange';

// Protected Route Component
const ProtectedRoute = ({ children, userType }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center h-screen">
      <div className="text-xl">Loading...</div>
    </div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (userType && user.user_type !== userType) {
    return <Navigate to="/" />;
  }

  return children;
};

// Onboarded Driver Route - requires completed onboarding
const OnboardedDriverRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const [onboardingStatus, setOnboardingStatus] = React.useState(null);
  const [checking, setChecking] = React.useState(true);

  React.useEffect(() => {
    const checkOnboarding = async () => {
      if (user && user.user_type === 'driver') {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/drivers/onboarding-status`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          // Handle 401 - token expired
          if (response.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
            return;
          }

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          const data = await response.json();
          setOnboardingStatus(data);
        } catch (error) {
          console.error('Error checking onboarding status:', error);
        }
      }
      setChecking(false);
    };

    if (!loading) {
      checkOnboarding();
    }
  }, [user, loading]);

  if (loading || checking) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.user_type !== 'driver') {
    return <Navigate to="/" />;
  }

  // If onboarding not complete, redirect to onboarding
  if (onboardingStatus && !onboardingStatus.onboarding_complete) {
    return <Navigate to="/driver/onboarding" />;
  }

  return children;
};

// Not Onboarded Driver Route - only allows access if onboarding is NOT complete
// Redirects completed drivers to dashboard (prevents returning to onboarding page)
const NotOnboardedDriverRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const [checkingStatus, setCheckingStatus] = React.useState(true);
  const [isOnboarded, setIsOnboarded] = React.useState(false);

  React.useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user || user.user_type !== 'driver') {
        setCheckingStatus(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/drivers/onboarding-status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Handle 401 - token expired
        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        setIsOnboarded(data.onboarding_complete);
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      } finally {
        setCheckingStatus(false);
      }
    };

    if (!loading) {
      checkOnboardingStatus();
    }
  }, [user, loading]);

  if (loading || checkingStatus) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.user_type !== 'driver') {
    return <Navigate to="/" />;
  }

  // Key logic: If already onboarded, redirect to dashboard
  if (isOnboarded) {
    return <Navigate to="/driver/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
        <Router>
          <div className="min-h-screen bg-gray-50">
            <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/signup/driver" element={<DriverSignup />} />
            <Route path="/signup/rider" element={<RiderSignup />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<RequestPasswordReset />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/email-change-pending" element={<EmailChangePending />} />
            <Route path="/verify-email-change" element={<VerifyEmailChange />} />
            <Route path="/request-ride" element={<RequestRide />} />
            <Route path="/rides/:rideId/complete" element={<RideCompletionPage />} />
            <Route path="/rides/:rideId/tip" element={<RideTipPage />} />
            <Route path="/day-bookings/:bookingId/tip" element={<RideTipPage />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/terms/driver" element={<DriverTermsOfUse />} />
            <Route path="/terms/rider" element={<RiderTermsOfUse />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />


            {/* Driver Onboarding Routes - redirect to dashboard if already completed */}
            <Route
              path="/driver/onboarding"
              element={
                <NotOnboardedDriverRoute>
                  <DriverOnboarding />
                </NotOnboardedDriverRoute>
              }
            />
            <Route
              path="/driver/onboarding/profile"
              element={
                <NotOnboardedDriverRoute>
                  <ProfileSetup />
                </NotOnboardedDriverRoute>
              }
            />
            <Route
              path="/driver/stripe-onboarding"
              element={
                <ProtectedRoute userType="driver">
                  <StripeOnboarding />
                </ProtectedRoute>
              }
            />

            {/* Driver Routes (require completed onboarding) */}
            <Route
              path="/driver/dashboard"
              element={
                <OnboardedDriverRoute>
                  <DriverDashboard />
                </OnboardedDriverRoute>
              }
            />
            <Route
              path="/driver/profile"
              element={
                <ProtectedRoute userType="driver">
                  <DriverProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/availability"
              element={
                <ProtectedRoute userType="driver">
                  <DriverAvailability />
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/pricing"
              element={
                <ProtectedRoute userType="driver">
                  <DriverPricing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/day-rate-pricing"
              element={
                <ProtectedRoute userType="driver">
                  <DayRatePricing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/ride-requests"
              element={
                <ProtectedRoute userType="driver">
                  <DriverRideRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/subscription"
              element={
                <ProtectedRoute userType="driver">
                  <SubscriptionManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/ride-history"
              element={
                <ProtectedRoute userType="driver">
                  <DriverRideHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/calendar"
              element={
                <ProtectedRoute userType="driver">
                  <DriverCalendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/onboarding/complete"
              element={
                <ProtectedRoute userType="driver">
                  <StripeOnboardingComplete />
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/payouts"
              element={
                <ProtectedRoute userType="driver">
                  <PayoutHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/help-support"
              element={
                <ProtectedRoute userType="driver">
                  <DriverHelpSupport />
                </ProtectedRoute>
              }
            />

            {/* Rider Routes */}
            <Route
              path="/rider/dashboard"
              element={
                <ProtectedRoute userType="rider">
                  <RiderDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rider/profile"
              element={
                <ProtectedRoute userType="rider">
                  <RiderProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rider/browse-drivers"
              element={
                <ProtectedRoute userType="rider">
                  <BrowseDrivers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/request-ride/:driverId"
              element={
                <ProtectedRoute userType="rider">
                  <RequestRide />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rider/request-day-booking"
              element={
                <ProtectedRoute userType="rider">
                  <RequestDayBooking />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rider/history"
              element={
                <ProtectedRoute userType="rider">
                  <RiderRideHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rider/help-support"
              element={
                <ProtectedRoute userType="rider">
                  <RiderHelpSupport />
                </ProtectedRoute>
              }
            />

            {/* Shared Routes */}
            <Route path="/profile/settings" element={<ProfileSettings />} />

            {/* Default Route */}
            <Route path="/" element={<Navigate to="/login" />} />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
  );
}

export default App;